import { el, clear } from "../components/dom.js";
import { movieSeriesBlock } from "../components/movie-series.js";
import { progressField, priceNoteField, loanField, startAgainButton } from "../components/tracking-fields.js";
import { reviewField, tagsField } from "../components/review-tags.js";
import { tagSuggestions } from "../utils/filters.js";
import { inTheNews } from "../components/in-the-news.js";
import { offerUndoDelete } from "../components/undo.js";
import { peopleRow } from "../components/people-row.js";
import { historySection } from "../components/history-section.js";
import {
	ENGAGEMENT_STATUSES,
	OWNERSHIP_STATUSES,
	OWNERSHIP_FORMATS,
	TIMES_FINISHED,
	AWARDS,
	awardLabel,
	statusLabel,
} from "../components/status-badge.js";
import {
	getMovieById,
	updateMovie,
	deleteMovie,
	setMovieRating,
	setMovieFormats,
	setMovieTimesFinished,
	setMovieAwards,
	setMovieNote,
	formatRuntime,
	NOTE_MAX_LENGTH,
} from "../db/movies.js";
import {
	refreshMovie as refreshMovieCatalog,
	movieNeedsRefresh,
	movieRecentlyChecked,
} from "../db/catalog-refresh.js";
import { getGroupsContainingItem } from "../db/groups.js";
import { groupsSection } from "../components/groups-section.js";
import { formatDate, formatReleaseDate, todayDate } from "../utils/dates.js";
import { navigate, navigateBack, rerenderRoute } from "../main.js";
import { openItemMenu } from "../components/item-menu.js";
import { overriddenFields } from "../utils/overrides.js";
import { statusSticker } from "../components/magazine/status.js";
import { MAG, magGlass } from "../components/magazine/kit.js";
import {
	magDetailHero,
	magSummary,
	magField,
	magSelectRow,
	magChips,
	magSingleChips,
	magRating,
	magNote,
	magDeleteFooter,
	magDateLine,
} from "../components/magazine/detail.js";

// The movie detail screen — the game detail minus platforms/editions/series,
// plus a read-only "facts" panel for the OMDb metadata (people, runtime, age
// rating, IMDb score).

export async function renderMovieDetail(root, id) {
	const movie = await getMovieById(id);
	if (!movie) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Movie not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/movies") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);
	mount(view, movie);

	// Keep the catalog side honest: a film whose date could still move is
	// re-checked against OMDb on open — at most once a day per film; the daily
	// pass at boot usually got there first. Silent: if anything changed, re-render.
	if (
		movie.imdb_id &&
		movieNeedsRefresh(movie, todayDate()) &&
		!movieRecentlyChecked(movie.imdb_id)
	) {
		refreshMovieCatalog(movie)
			.then((updated) => {
				if (updated) mount(view, updated);
			})
			.catch(() => {});
	}
}

async function mount(view, movie) {
	// Re-mounting rebuilds the whole view. Capture scroll up-front, then do all the
	// async work WHILE THE OLD VIEW IS STILL ON SCREEN; only clear + append + restore
	// synchronously at the very end, so the browser never paints a cleared/top state.
	const scrollY = window.scrollY;

	const groups = await getGroupsContainingItem(movie);

	// ---- feature hero ----
	const kickerBits = ["Movie"];
	if (movie.release_year) kickerBits.push(String(movie.release_year));
	if ((movie.genres ?? []).length > 0) kickerBits.push(movie.genres.slice(0, 2).join(" · "));
	if (overriddenFields(movie).length > 0) kickerBits.push("Edited");

	const figures = [{ value: movie.rating != null ? movie.rating : "—", label: "Rating" }];
	const runtime = formatRuntime(movie.runtime);
	if (runtime) figures.push({ value: runtime, label: "Runtime" });
	else if (movie.release_date) figures.push({ value: formatReleaseDate(movie.release_date), label: "Released" });

	const hero = magDetailHero(movie, {
		kicker: kickerBits.join(" · "),
		title: movie.title,
		sticker: statusSticker(movie.engagement_status, "movie"),
		figures,
		tag: (movie.awards ?? [])[0] ? awardLabel(movie.awards[0]) : null,
		onBack: () => navigateBack("/movies"),
		// "⋯": edit details / re-link / undo / reset (components/item-menu.js). A
		// re-link swaps the catalog underneath, so rebuild the whole route for that.
		onMenu: () =>
			openItemMenu({
				mediaType: "movie",
				record: movie,
				onChanged: (updated, { relinked } = {}) =>
					relinked ? rerenderRoute() : mount(view, updated),
			}),
	});

	// ---- fields ----
	const ownsIt =
		movie.ownership_status === "have_it" || movie.ownership_status === "had_it";

	const engagementField = magField(
		"Watch it?",
		magSelectRow(ENGAGEMENT_STATUSES, movie.engagement_status, async (value) => {
			mount(view, await updateMovie(movie.id, { engagement_status: value }));
		}, (v) => statusLabel(v, "movie")),
		{ index: 0 },
	);

	const ownershipField = magField(
		"Got it?",
		magSelectRow(OWNERSHIP_STATUSES, movie.ownership_status ?? "have_it", async (value) => {
			mount(view, await updateMovie(movie.id, { ownership_status: value }));
		}, (v) => statusLabel(v, "movie")),
		{ index: 1 },
	);

	const selectedFormats = new Set(movie.formats ?? []);
	const formatField = ownsIt && magField(
		"Format",
		magChips(OWNERSHIP_FORMATS, selectedFormats, async (next) => {
			mount(view, await setMovieFormats(movie.id, Array.from(next)));
		}, (v) => statusLabel(v)),
		{ index: 2 },
	);

	const ratingField = magField(
		"Rating",
		magRating(movie.rating ?? null, async (value) => {
			mount(view, await setMovieRating(movie.id, value));
		}),
		{ index: 3 },
	);

	const selectedAwards = new Set(movie.awards ?? []);
	const awardsField = magField(
		"Awards",
		magChips(AWARDS.map((a) => a.value), selectedAwards, async (next) => {
			mount(view, await setMovieAwards(movie.id, Array.from(next)));
		}, awardLabel),
		{ index: 4 },
	);

	const timesFinishedField = movie.engagement_status === "done" && magField(
		"Times watched",
		magSingleChips(TIMES_FINISHED, movie.times_finished ?? null, async (value) => {
			mount(view, await setMovieTimesFinished(movie.id, value));
		}),
		{ index: 2 },
	);

	const noteField = magField(
		"Notes",
		magNote(movie.note ?? "", {
			maxLength: NOTE_MAX_LENGTH,
			placeholder: 'A quick note — e.g. "saw it in the cinema with Sam"',
			onSave: (v) => setMovieNote(movie.id, v),
		}),
		{ index: 5 },
	);

	const groupsField = magField(
		"Groups",
		await groupsSection(movie, groups, async () => mount(view, await getMovieById(movie.id))),
		{ index: 1 },
	);

	const dates = magDateLine([
		["First watched", formatDate(movie.date_first_completed)],
		["Last watched", formatDate(movie.last_engaged_date)],
		["Added", formatDate(movie.created_at)],
	]);

	const footer = magDeleteFooter("Delete from library", async () => {
		if (!confirm(`Delete "${movie.title}" from your list?`)) return;
		await deleteMovie(movie.id);
		navigate("/movies");
		offerUndoDelete("movie", movie, { reopenRoute: `/movies/${movie.id}` });
	});

	clear(view);
	view.append(
		hero,
		magSummary(movie.summary) || "",
		inTheNews(movie),
		peopleRow("movie", movie.imdb_id),
		factsPanel(movie) || "",
		engagementField,
		ownershipField,
		formatField || "",
		ratingField,
		awardsField,
		timesFinishedField || "",
		noteField,
		tagsField("movie", movie, { suggestions: tagSuggestions("movie") }),
		reviewField("movie", movie),
		progressField("movie", movie),
		priceNoteField("movie", movie),
		loanField("movie", movie),
		startAgainButton("movie", movie),
		groupsField,
		dates,
		movieSeriesBlock(movie),
		historySection(movie),
		footer,
	);

	window.scrollTo(0, scrollY);
}

// Read-only catalog facts (OMDb): people, release date, age rating, IMDb score.
// Returns null when there's nothing to show (e.g. a record synced without them).
function factsPanel(movie) {
	const rows = [
		["Directed by", (movie.directors ?? []).join(", ")],
		["Written by", (movie.writers ?? []).join(", ")],
		["Starring", (movie.cast ?? []).join(", ")],
		["Released", movie.release_date ? formatReleaseDate(movie.release_date) : ""],
		["Rated", movie.rated ?? ""],
		["IMDb", movie.imdb_rating != null ? `${movie.imdb_rating} / 10` : ""],
	].filter(([, v]) => v);
	if (rows.length === 0) return null;

	const list = rows.map(([label, value]) =>
		el(
			"div",
			{ style: { display: "flex", gap: "12px", alignItems: "baseline" } },
			el("span", {
				style: {
					flex: "none", width: "86px",
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8.5px",
					letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim,
				},
			}, label),
			el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "500", fontSize: "12.5px",
					lineHeight: "1.4", color: "#cfcfd6", minWidth: "0",
				},
			}, value),
		),
	);
	return el(
		"div",
		{ style: { padding: "12px 16px 0" } },
		magGlass(list, {
			style: { display: "flex", flexDirection: "column", gap: "7px", padding: "12px 14px" },
		}),
	);
}
